import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/** Event series — a brand or recurring event (`IIGCE`) whose editions are tenants. */

export const seriesSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  editions: z.number(),
  template: z.object({ id: z.uuid(), name: z.string() }).nullable(),
})

export type Series = z.infer<typeof seriesSchema>

export function fetchSeries(signal?: AbortSignal): Promise<{ items: Series[] }> {
  return api.get('/v1/platform/series', {
    schema: z.object({ items: z.array(seriesSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function createSeries(input: { slug: string; name: string }): Promise<Series> {
  return api.post('/v1/platform/series', { schema: seriesSchema, body: input })
}

export function renameSeries(id: string, name: string): Promise<Series> {
  return api.patch(`/v1/platform/series/${id}`, { schema: seriesSchema, body: { name } })
}

const moneySchema = z.object({ currency: z.string(), amount: z.string() })

export type Money = z.infer<typeof moneySchema>

const measuresSchema = z.object({
  registrations: z.number().nullable(),
  completedRegistrations: z.number().nullable(),
  delegates: z.number(),
  exhibitors: z.number().nullable(),
  confirmedBooths: z.number().nullable(),
  invoiced: z.array(moneySchema).nullable(),
  paid: z.array(moneySchema).nullable(),
})

export type Measures = z.infer<typeof measuresSchema>

export const seriesDetailSchema = seriesSchema.extend({
  /** Oldest first. */
  editionList: z.array(
    z.object({
      id: z.uuid(),
      slug: z.string(),
      name: z.string(),
      status: z.string(),
      createdAt: z.string(),
      planName: z.string(),
      measures: measuresSchema,
    }),
  ),
})

export type SeriesDetail = z.infer<typeof seriesDetailSchema>
export type SeriesEdition = SeriesDetail['editionList'][number]

export function fetchSeriesDetail(seriesId: string, signal?: AbortSignal): Promise<SeriesDetail> {
  return api.get(`/v1/platform/series/${seriesId}`, {
    schema: seriesDetailSchema,
    ...(signal ? { signal } : {}),
  })
}

/** Set the template edition of a series, or clear it with `null`. */
export function setSeriesTemplate(seriesId: string, tenantId: string | null): Promise<Series> {
  return api.put(`/v1/platform/series/${seriesId}/template`, {
    schema: seriesSchema,
    body: { tenantId },
  })
}
